import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { Loader2, Search } from 'lucide-react';
import { useRef } from 'react';
import { DatePanel } from './date-panel';
import { DestinationPanel } from './destination-panel';
import { CTA, ZONE } from './search-bar.styles';
import { useSearchBarContext } from './use-search-bar';

interface DesktopPillProps {
  className?: string;
}

/**
 * The desktop surface: one pill, three zones.
 *
 * Positioning splits Radix's two roles rather than measuring the DOM. The
 * destination panel is anchored to the *pill* — so it comes out flush and exactly
 * pill-width via `--radix-popover-trigger-width` — while the trigger stays the
 * zone button, which keeps aria-haspopup/expanded/controls and focus return for
 * free. The previous implementation read getBoundingClientRect() during render
 * and offset the panel by a hard-coded 3rem, which drifted on resize.
 */
export function DesktopPill({ className }: DesktopPillProps) {
  const { openZone, openZoneAt, close, selectedSite, dateValue, submit, isSubmitting, submitError, form } = useSearchBarContext();

  const pillRef = useRef<HTMLDivElement>(null);
  const hasSiteError = Boolean(form.formState.errors.site);

  const dateLabel = dateValue ? format(parseISO(dateValue), 'dd/MM/yyyy') : 'Thêm ngày';

  /**
   * Clicking a sibling zone is a switch, not a dismissal. Without this, the open
   * popover's interact-outside fires before the other zone's click, so the panel
   * closes and reopens on the next tick — a visible flash on every switch.
   */
  const keepOpenOnZoneSwitch = (event: Event) => {
    if (pillRef.current?.contains(event.target as Node)) event.preventDefault();
  };

  return (
    <div className={className}>
      <Popover open={openZone === 'destination'} onOpenChange={(open) => (open ? openZoneAt('destination') : close())}>
        <PopoverAnchor asChild>
          {/* No overflow-hidden: it would clip the open zone's lift shadow. */}
          <div ref={pillRef} className={ZONE.pill}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-open={openZone === 'destination'}
                aria-invalid={hasSiteError}
                className={cn(ZONE.base, 'flex-[1.4]')}
              >
                <span className={ZONE.label}>Điểm đến</span>
                <span className={selectedSite ? ZONE.valueFilled : ZONE.valueEmpty}>{selectedSite?.name ?? 'Bạn muốn đi đâu?'}</span>
              </button>
            </PopoverTrigger>

            <span aria-hidden="true" className={cn(ZONE.divider, openZone && 'opacity-0')} />

            <Popover open={openZone === 'date'} onOpenChange={(open) => (open ? openZoneAt('date') : close())}>
              <PopoverTrigger asChild>
                <button type="button" data-open={openZone === 'date'} className={ZONE.base}>
                  <span className={ZONE.label}>Ngày đi</span>
                  <span className={dateValue ? ZONE.valueFilled : ZONE.valueEmpty}>{dateLabel}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={14}
                onInteractOutside={keepOpenOnZoneSwitch}
                className="rounded-card border-pill-border shadow-zone z-[51] w-auto p-3"
              >
                <DatePanel variant="popover" className="w-[19rem]" />
              </PopoverContent>
            </Popover>

            <span aria-hidden="true" className={cn(ZONE.divider, openZone && 'opacity-0')} />

            <div className="flex shrink-0 items-center pr-0.5 pl-2">
              <button type="button" onClick={submit} disabled={isSubmitting} aria-busy={isSubmitting} className={cn(CTA.base, 'h-12 px-7')}>
                {isSubmitting ? <Loader2 className="size-4.5 animate-spin motion-reduce:animate-none" /> : <Search className="size-4.5" />}
                <span className="hidden lg:inline">{isSubmitting ? 'Đang tìm…' : 'Tìm kiếm'}</span>
              </button>
            </div>
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          sideOffset={14}
          onInteractOutside={keepOpenOnZoneSwitch}
          // The panel focuses its own search field; letting Radix focus the first
          // element would land on the first destination card instead.
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="rounded-card border-pill-border shadow-zone z-[51] w-(--radix-popover-trigger-width) p-4"
        >
          <DestinationPanel variant="popover" />
        </PopoverContent>
      </Popover>

      {/* Below the pill, never inside it — an inline message would change the
          pill's height and shove the hero layout on every validation failure. */}
      {(submitError || hasSiteError) && (
        <p role="alert" className="text-destructive mt-2 px-4 text-sm">
          {submitError ?? form.formState.errors.site?.message}
        </p>
      )}
    </div>
  );
}
